import { Suspense, useState, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch } from 'universal-scripts'
import {
  addItem,
  closeItemEditor,
  replaceItem
} from 'src/store/slices/dashboard'
import { useQueryColumns } from '@hooks/useQuery'
import { isGeoJsonColumn } from 'src/utils/chartTransformers'
import type {
  AllowedColSpan,
  ChartType,
  ValueOverride
} from 'src/types/dashboard'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import Loader from '@components/Loader/Loader'
import AxisMapping from './AxisMapping'
import EmptyChartState from '@components/GridLayout/WidgetView/EmptyChartState/EmptyChartState'
import QueryNotFoundFallback from './QueryNotFoundFallback'
import ChartFormLoader from './ChartFormLoader'
import { CustomError } from '@components/ErrorBoundary/ErrorMessage'
import ChartOverridesEditor from './ChartOverridesEditor'

interface ChartFormProps {
  initial: {
    type: ChartType
    title: string
    description: string
    xAxis: string[]
    yAxis: string[]
    geoColumn?: string
    overrides?: Record<string, ValueOverride>
  }
  itemId: string | null
  query: string
  existingWidth?: AllowedColSpan
}

function ChartFormInner({
  initial,
  itemId,
  query,
  existingWidth
}: ChartFormProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const { colSpec } = useQueryColumns(query)

  const geoColumns = useMemo(
    () => (colSpec ?? []).filter(isGeoJsonColumn).map((c) => c.name),
    [colSpec]
  )
  const hasGeoColumn = geoColumns.length > 0

  const [type, setType] = useState<ChartType>(initial.type)
  const [title, setTitle] = useState(initial.title)
  const [description, setDescription] = useState(initial.description)
  const [xAxis, setXAxis] = useState<string[]>(initial.xAxis)
  const [yAxis, setYAxis] = useState<string[]>(initial.yAxis)
  const [geoColumn, setGeoColumn] = useState<string | undefined>(
    initial.geoColumn
  )
  const [overrides, setOverrides] = useState<Record<string, ValueOverride>>(
    initial.overrides ?? {}
  )

  useEffect(() => {
    if (!itemId && hasGeoColumn) {
      setType('map-chart')
    }
  }, [itemId, hasGeoColumn])

  useEffect(() => {
    if (!geoColumn && geoColumns.length > 0) {
      setGeoColumn(geoColumns[0])
    }
  }, [geoColumns, geoColumn])

  const axisIsValid = (axis: string[]) =>
    axis.length > 0 && axis.every((col) => col.trim() !== '')

  const isValid =
    title.trim() !== '' && axisIsValid(xAxis) && axisIsValid(yAxis)

  const handleApply = () => {
    if (!isValid) return

    const width: AllowedColSpan = existingWidth ?? 4

    const itemData = {
      type,
      width,
      title: title.trim(),
      description: description.trim(),
      query,
      xAxis: xAxis.map((c) => c.trim()),
      yAxis: yAxis.map((c) => c.trim()),
      geoColumn: type === 'map-chart' ? geoColumn : undefined,
      overrides: Object.keys(overrides).length > 0 ? overrides : undefined
    }

    if (itemId) {
      dispatch(replaceItem({ itemId, item: { itemId, ...itemData } }))
    } else {
      dispatch(addItem({ itemId: crypto.randomUUID(), ...itemData }))
    }

    dispatch(closeItemEditor())
  }

  const handleCancel = () => dispatch(closeItemEditor())

  const baseChartTypes: ChartType[] = ['bar-chart', 'line-chart', 'pie-chart']
  const chartTypes: ChartType[] =
    hasGeoColumn || type === 'map-chart'
      ? [...baseChartTypes, 'map-chart']
      : baseChartTypes

  return (
    <aside id="item-editor-panel">
      <header className="item-editor-header">
        <h3>
          <FormattedMessage id="item-editor.chart.editor-title" />
        </h3>
        <div className="item-editor-actions">
          <button
            className="highlight-button"
            onClick={handleApply}
            disabled={!isValid}
          >
            <span>
              <FormattedMessage id="node-editor.button.apply" />
            </span>
          </button>
          <span
            className="icon icon-cross standart-button"
            onClick={handleCancel}
          />
        </div>
      </header>
      <div className="item-editor-body">
        <label>
          <h4>
            <FormattedMessage id="item-editor.chart.title" />
          </h4>
          <input
            type="text"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'item-editor.chart.title.placeholder'
            })}
          />
        </label>
        <label>
          <h4>
            <FormattedMessage id="item-editor.chart.description" />
          </h4>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'item-editor.chart.description.placeholder'
            })}
          />
        </label>
        <label>
          <h4>
            <FormattedMessage id="item-editor.chart.type" />
          </h4>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ChartType)}
          >
            {chartTypes.map((t) => (
              <option key={t} value={t}>
                {intl.formatMessage({ id: `dashboard.chart.${t}` })}
              </option>
            ))}
          </select>
        </label>

        {type === 'map-chart' && geoColumns.length > 0 && (
          <label>
            <h4>GeoJSON</h4>
            {geoColumns.length === 1 ? (
              <input type="text" value={geoColumns[0]} disabled />
            ) : (
              <select
                value={geoColumn || geoColumns[0]}
                onChange={(e) => setGeoColumn(e.target.value)}
              >
                {geoColumns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}

        <ErrorBoundary errorComponent={EmptyChartState}>
          <Suspense fallback={<Loader mini />}>
            <AxisMapping
              query={query}
              chartType={type}
              xAxis={xAxis}
              yAxis={yAxis}
              onChangeXAxis={setXAxis}
              onChangeYAxis={setYAxis}
            />
          </Suspense>
          <ChartOverridesEditor
            query={query}
            chartType={type}
            xAxis={xAxis}
            yAxis={yAxis}
            overrides={overrides}
            onChangeOverrides={setOverrides}
          />
        </ErrorBoundary>
      </div>
    </aside>
  )
}

export default function ChartForm(props: ChartFormProps) {
  return (
    <ErrorBoundary
      errorComponent={({ error }: { error: CustomError }) => (
        <QueryNotFoundFallback itemId={props.itemId} error={error} />
      )}
    >
      <Suspense fallback={<ChartFormLoader />}>
        <ChartFormInner {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}
